import { useTranslation } from 'react-i18next';
import { PauseCircle, PlugZap } from 'lucide-react';
import { uspacyDocumentVersionAtLeast, type UspacyLegalDocuments } from '@aica/api-types';
import type { UspacyProcessingStateResponse } from '@/lib/api/domains/organizations.api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  UspacyLegalActions,
  type UspacyLegalActionsState,
} from '@/components/legal/UspacyLegalActions';
import { cn } from '@/lib/utils';

interface UspacyProcessingCardProps {
  installation: NonNullable<UspacyProcessingStateResponse['installation']>;
  processing: UspacyProcessingStateResponse['processing'];
  legalDocuments: UspacyLegalDocuments;
  canManage: boolean;
  actionsDisabled: boolean;
  mutationPending: boolean;
  legalActions: UspacyLegalActionsState;
  allLegalActions: boolean;
  onLegalActionChange: (action: keyof UspacyLegalActionsState, checked: boolean) => void;
  onPause: () => void;
  onWithdraw: () => void;
  onReauthorize: () => void;
}

/** Organization processing controls, deliberately separate from consent cards. */
export function UspacyProcessingCard({
  installation,
  processing,
  legalDocuments,
  canManage,
  actionsDisabled,
  mutationPending,
  legalActions,
  allLegalActions,
  onLegalActionChange,
  onPause,
  onWithdraw,
  onReauthorize,
}: UspacyProcessingCardProps) {
  const { t } = useTranslation(['settings', 'common']);
  // A portal with no processing row at all is the state that needs an
  // instruction MOST, not one that forbids giving one: an install straight from
  // the Marketplace before anyone onboarded, an activation that failed and
  // rolled back, or a row predating the processing tables. Requiring a row here
  // left those installations showing a "pending" badge and not a single
  // control — and for a portal already linked to an existing Aelo organization
  // this card is the only remaining way in, because the placement answers
  // CUSTOMER_ADMIN_ACTION_REQUIRED.
  //
  // An ACTIVE installation whose accepted instruction is OLDER than the current
  // one needs it too. Features gated on a later version (conversation themes
  // from 1.10, #845) stay off for it until the Customer accepts that version,
  // and without this the only way to reach the clickwrap was to pause
  // processing first. Older, not different: after a rollback the database may
  // hold a NEWER acceptance than this deployment serves, and offering the
  // clickwrap then would overwrite it with an earlier version.
  const currentInstructionVersion = legalDocuments.processingInstruction.version;
  const instructionOutdated =
    processing?.state === 'active' &&
    !uspacyDocumentVersionAtLeast(
      processing.instructionVersion,
      currentInstructionVersion
    );
  const needsAuthorization =
    !processing ||
    processing.state !== 'active' ||
    processing.subscriptionStatus !== 'active' ||
    instructionOutdated;

  return (
    <Card className="p-4" data-testid="uspacy-processing-card">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <PlugZap className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-medium text-fg">
              {t('consent.uspacy.title', 'Uspacy organization processing')}
            </h3>
          </div>
          <p className="mt-1 text-xs text-fg-muted">
            {t(
              'consent.uspacy.description',
              'This organization-level instruction is separate from participant consent. Pausing or withdrawing blocks new Uspacy calls before transcription and analysis.'
            )}
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {/* `bg-bg-subtle`, not `bg-bg-muted`: the latter is never declared
                in @theme, so the class generates nothing and the domain chip
                rendered as loose text next to a real state pill. */}
            <span className="rounded bg-bg-subtle px-2 py-0.5 text-fg-muted">
              {installation.portalDomain}
            </span>
            <span
              className={cn(
                'rounded px-2 py-0.5 font-medium',
                processing?.state === 'active'
                  ? 'bg-success-dim text-success-fg'
                  : 'bg-warning-dim text-warning-fg'
              )}
            >
              {/* The enum is compliance-facing copy, not a technical id: it
                  used to render `active`/`paused`/`withdrawn` verbatim, so the
                  one chip that tells an admin whether their organization's
                  processing instruction is in force stayed English in ru/uk/es. */}
              {processing
                ? t(`consent.uspacy.state.${processing.state}`, {
                    defaultValue: processing.state,
                  })
                : t('consent.uspacy.pending', 'pending')}
            </span>
            {processing?.instructionUrl && processing.instructionVersion ? (
              <a
                href={processing.instructionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline"
              >
                {t('consent.uspacy.instruction', 'Processing instruction')} v
                {processing.instructionVersion}
              </a>
            ) : null}
          </div>
        </div>

        {canManage && processing?.state === 'active' ? (
          <div className="flex shrink-0 gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={actionsDisabled || mutationPending}
              onClick={onPause}
            >
              <PauseCircle className="mr-1 h-4 w-4" />
              {t('consent.uspacy.pause', 'Pause')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={actionsDisabled || mutationPending}
              className="text-danger hover:text-danger"
              onClick={onWithdraw}
            >
              {t('consent.uspacy.withdraw', 'Withdraw instruction')}
            </Button>
          </div>
        ) : null}
      </div>

      {canManage && needsAuthorization ? (
        <div
          className="mt-4 space-y-3 border-t border-border pt-4"
          data-testid="uspacy-reauthorization"
        >
          {instructionOutdated ? (
            <p className="text-xs text-fg" data-testid="uspacy-instruction-outdated">
              {t(
                'consent.uspacy.instructionOutdated',
                'The processing instruction has been updated to version {{version}}. Features added with later versions of the instruction may stay off for this organization until you accept the current one.',
                { version: currentInstructionVersion }
              )}
            </p>
          ) : null}
          <p className="text-xs text-fg-muted">
            {t(
              'consent.uspacy.reauthorizeIntro',
              'Review the current documents and give a new organization instruction. Every control starts unchecked.'
            )}
          </p>
          <UspacyLegalActions
            documents={legalDocuments}
            actions={legalActions}
            onActionChange={onLegalActionChange}
          />
          <Button
            size="sm"
            disabled={actionsDisabled || mutationPending || !allLegalActions}
            onClick={onReauthorize}
          >
            <PlugZap className="mr-1 h-4 w-4" />
            {t('consent.uspacy.reauthorize', 'Reauthorize processing')}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
